import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lightning, Play, CaretDown, CaretUp, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const LIMITS = [
  { v: 100, label: "100 sets" },
  { v: 500, label: "500 sets" },
  { v: 2000, label: "2,000 sets" },
  { v: 30000, label: "ALL LEGO" },
];
const RUNNING = ["running", "queued", "waiting_for_tokens"];

/**
 * Deals-style scan bar for the Opportunity Board. Scope = the board's
 * current theme filter (none = the whole catalog). ASIN ONLY resolves
 * ASINs + Amazon Buy Box via Keepa; SCAN AMAZON + BL also scrapes BrickLink
 * so rows flip fully LIVE. Checkpointed: already-attempted sets are skipped.
 */
export function BoardScanPanel({ themes, onDataChanged, selected, onClearSelection }) {
  const [open, setOpen] = useState(() => localStorage.getItem("board_scan_open") === "true");
  const [stats, setStats] = useState(null);
  const [job, setJob] = useState(null);
  const [limit, setLimit] = useState(() => Number(localStorage.getItem("board_scan_limit")) || 500);
  const [continuous, setContinuous] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const [resumable, setResumable] = useState(null);
  const themeKey = (themes || []).join("|");

  const loadStats = () => api.asinResolverStats(null, themes || []).then(setStats).catch(() => {});
  useEffect(() => { if (open) loadStats(); /* eslint-disable-next-line */ }, [open, themeKey]);
  useEffect(() => { api.asinResolverLastIncomplete().then(r => setResumable(r.job)).catch(() => {}); }, []);

  useEffect(() => {
    if (!job || !RUNNING.includes(job.status)) return;
    const id = setInterval(async () => {
      try {
        const j = await api.getScan(job.id);
        setJob(j);
        if (!RUNNING.includes(j.status)) { loadStats(); onDataChanged?.(); }
      } catch {}
    }, 2500);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [job?.id, job?.status]);

  const start = async (fetchBricklink, setNumbers = null) => {
    try {
      const j = await api.asinResolverRun(setNumbers ? {
        // Ticked rows: force — ignore checkpoint + year/piece eligibility
        set_numbers: setNumbers, limit: setNumbers.length, continuous, skip_attempted: false,
        retry_failed: true, fetch_bricklink: fetchBricklink,
      } : {
        themes: themes?.length ? themes : null,
        limit, continuous, retry_failed: retryFailed, skip_attempted: true, fetch_bricklink: fetchBricklink,
      });
      setJob(j); setResumable(null);
      const scope = setNumbers ? `${setNumbers.length} selected` : `${themes?.length ? themes.join(", ") : "ALL LEGO"} · up to ${limit.toLocaleString()} sets`;
      toast.info(`${fetchBricklink ? "Amazon + BrickLink" : "ASIN"} scan started · ${scope}`);
      if (setNumbers) onClearSelection?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start scan"); }
  };
  const selCount = selected?.size || 0;
  const stop = async () => { if (job) { try { await api.asinResolverCancel(job.id); toast.info("Stopping…"); } catch {} } };
  const resume = async () => {
    try { const j = await api.asinResolverResume(); setJob(j); setResumable(null); toast.info("Resumed last scan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Nothing to resume"); }
  };
  const toggleOpen = () => { setOpen(o => { localStorage.setItem("board_scan_open", String(!o)); return !o; }); };
  const pickLimit = (v) => { setLimit(v); localStorage.setItem("board_scan_limit", String(v)); };

  const running = job && RUNNING.includes(job.status);
  const pct = job?.total ? Math.round((job.progress / job.total) * 100) : 0;
  const scopeLabel = themes?.length ? themes.join(", ") : "ALL LEGO";

  return (
    <div className="border-b border-[#E4E4E0] bg-[#FFFFFF]" data-testid="board-scan-panel">
      <button onClick={toggleOpen} data-testid="board-scan-toggle"
        className="w-full px-4 py-2 flex items-center gap-2 text-left hover:bg-[#F6F6F3] transition-colors">
        <Lightning size={13} weight="fill" className="text-[#E3000B]" />
        <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-800">Scans</span>
        <span className="font-mono text-[10px] text-gray-500">
          scope: <span className="text-[#E3000B]">{scopeLabel}</span>
          {stats && <> · {stats.verified_via_keepa.toLocaleString()} resolved · {stats.not_listed_via_keepa.toLocaleString()} not on Amazon · <span className="text-gray-900">{stats.eligible_to_scan.toLocaleString()}</span> left to scan</>}
        </span>
        {running && <span className="ml-2 font-mono text-[10px] text-[#E3000B] animate-pulse">RUNNING {pct}%</span>}
        <span className="ml-auto text-gray-500">{open ? <CaretUp size={12} /> : <CaretDown size={12} />}</span>
      </button>

      {selCount > 0 && (
        <div className="mx-4 mb-2 border border-[#E3000B]/40 bg-[#E3000B]/[0.06] rounded-sm p-2 flex flex-wrap items-center gap-2" data-testid="board-selection-bar">
          <span className="font-mono text-xs text-[#E3000B] mr-2" data-testid="board-selection-count">{selCount} SELECTED</span>
          <Button onClick={() => start(false, Array.from(selected))} disabled={running} data-testid="board-scan-selected-asin"
            className="h-8 rounded-sm bg-transparent border border-[#D97706] text-[#D97706] font-bold uppercase tracking-wider text-[10px] hover:bg-[#D97706]/10 disabled:opacity-50">
            <Play size={10} weight="fill" className="mr-1" /> ASIN ONLY
          </Button>
          <Button onClick={() => start(true, Array.from(selected))} disabled={running} data-testid="board-scan-selected-amazon-bl"
            className="h-8 rounded-sm bg-[#D97706] text-gray-900 font-bold uppercase tracking-wider text-[10px] hover:bg-[#FFAB1F] disabled:opacity-50">
            <Play size={10} weight="fill" className="mr-1" /> SCAN AMAZON + BL
          </Button>
          <span className="font-mono text-[10px] text-gray-500">forces a fresh lookup — ignores the year / piece rule</span>
          <Button onClick={onClearSelection} variant="outline" data-testid="board-selection-clear"
            className="h-8 rounded-sm border-[#E4E4E0] bg-transparent text-[10px] font-mono uppercase text-gray-600 hover:border-zinc-500 ml-auto">CLEAR</Button>
        </div>
      )}

      {open && (
        <div className="px-4 pb-3 space-y-3">
          <div className="flex items-center flex-wrap gap-2">
            <Button onClick={() => start(false)} disabled={running} data-testid="board-scan-asin"
              title="Find ASIN + Amazon Buy Box via Keepa for sets not yet attempted. Skips resolved sets."
              className="h-9 rounded-sm bg-transparent border border-[#D97706] text-[#D97706] font-bold uppercase tracking-wider text-xs hover:bg-[#D97706]/10 disabled:opacity-50">
              <Play size={12} weight="fill" className="mr-1" /> ASIN ONLY
            </Button>
            <Button onClick={() => start(true)} disabled={running} data-testid="board-scan-amazon-bl"
              title="ASIN + Amazon Buy Box via Keepa AND BrickLink sealed price — rows go fully LIVE"
              className="h-9 rounded-sm bg-[#D97706] text-gray-900 font-bold uppercase tracking-wider text-xs hover:bg-[#FFAB1F] disabled:opacity-50">
              <Play size={12} weight="fill" className="mr-1" /> SCAN AMAZON + BL
            </Button>
            {running && (
              <Button onClick={stop} variant="outline" data-testid="board-scan-stop"
                className="h-9 rounded-sm border-[#FF3333] text-[#FF3333] font-bold uppercase tracking-wider text-xs hover:bg-[#FF3333]/10">STOP</Button>
            )}
            {!running && resumable && (
              <Button onClick={resume} variant="outline" data-testid="board-scan-resume"
                title={`Resume paused scan (${resumable.progress}/${resumable.total})`}
                className="h-9 rounded-sm border-[#E3000B] text-[#E3000B] font-bold uppercase tracking-wider text-xs hover:bg-[#E3000B]/10">
                <ArrowCounterClockwise size={12} className="mr-1" /> RESUME
              </Button>
            )}
            <select value={limit} onChange={e => pickLimit(Number(e.target.value))} data-testid="board-scan-limit"
              className="h-9 bg-[#F6F6F3] border border-[#E4E4E0] text-xs font-mono px-2 text-gray-800 rounded-sm">
              {LIMITS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
            </select>
            <label className="flex items-center gap-2 px-3 h-9 border border-[#E4E4E0] rounded-sm cursor-pointer" title="Sleep for Keepa token refills instead of pausing — for overnight runs">
              <span className="text-xs font-mono text-gray-600">Continuous</span>
              <Switch checked={continuous} onCheckedChange={setContinuous} className="data-[state=checked]:bg-[#E3000B] h-4 w-7" data-testid="board-scan-continuous" />
            </label>
            <label className="flex items-center gap-2 px-3 h-9 border border-[#E4E4E0] rounded-sm cursor-pointer" title="Re-check sets previously marked NOT ON AMAZON">
              <span className="text-xs font-mono text-gray-600">Retry not-found</span>
              <Switch checked={retryFailed} onCheckedChange={setRetryFailed} className="data-[state=checked]:bg-[#FF8C00] h-4 w-7" data-testid="board-scan-retry" />
            </label>
            <span className="ml-auto font-mono text-[10px] text-gray-500">Missing only · skips resolved sets · eligible = 2010–2025, ≥50 pcs · Keepa ≈ 10 tokens/set</span>
          </div>

          {job && (
            <div className="border border-[#E4E4E0] rounded-sm p-3 font-mono text-[11px]" data-testid="board-scan-job">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">{job.params?.fetch_bricklink ? "AMAZON + BL" : "ASIN"} · JOB STATUS</span>
                <span className={
                  job.status === "completed" ? "text-[#00A850]" : job.status === "failed" ? "text-[#FF3333]"
                    : ["paused", "waiting_for_tokens"].includes(job.status) ? "text-[#FF8C00]" : "text-[#E3000B]"
                }>{job.status.toUpperCase().replace(/_/g, " ")}</span>
              </div>
              <div className="w-full h-1.5 bg-[#F6F6F3] border border-[#E4E4E0] rounded-sm overflow-hidden">
                <div className="h-full bg-[#E3000B] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-gray-600">
                <span>{job.progress}/{job.total} processed</span>
                <span className="flex items-center gap-3">
                  <span className="text-[#00A850]" data-testid="board-scan-found">{job.opportunities_found ?? 0} with ASIN</span>
                  <span className="text-[#FF8C00]" data-testid="board-scan-not-found">{job.params?.not_found ?? 0} not on Amazon</span>
                </span>
              </div>
              {job.error && <div className="text-gray-500 mt-1">{job.error}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
